import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Field,
  Header,
  HeaderIconButton,
  HeaderText,
  Input,
  Message,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import {
  DISTANCE_UNIT_OPTIONS,
  DISTANCE_UNITS,
  convertDistanceToFeet,
  getDistanceUnit
} from '../utils/distanceUnits';
import { loadDefaultLocationRadius } from '../utils/defaultLocationRadius';
import useBackNavigation from '../utils/useBackNavigation';

const MapPanel = styled(Card)`
  position: relative;
  padding: 0;
  overflow: hidden;
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(260px, 320px) auto;
  gap: 0.85rem;
  align-items: end;
  margin: 1rem 0;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DistanceControls = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1fr);
  gap: 0.6rem;
`;

const UnitSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-height: 40px;
`;

const UnitOption = styled.button`
  min-height: 34px;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(251, 191, 36, 0.72)' : 'rgba(226, 232, 240, 0.14)')};
  border-radius: 999px;
  background: ${({ $selected }) => ($selected ? 'rgba(251, 191, 36, 0.18)' : 'rgba(15, 23, 42, 0.56)')};
  color: ${({ $selected }) => ($selected ? '#fbbf24' : 'rgba(226, 232, 240, 0.76)')};
  font: inherit;
  font-size: 0.76rem;
  font-weight: 950;
  letter-spacing: 0;
  text-transform: none;
  cursor: pointer;
`;

const UnitSeparator = styled.span`
  color: rgba(203, 213, 225, 0.46);
  font-weight: 950;
`;

const MapContainer = styled.div`
  position: relative;
  height: min(58vh, 460px);
  min-height: 340px;
`;

const LocateButton = styled.button`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.92);
  color: #f8fafc;
  cursor: pointer;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.28);

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.16s ease;
  }

  &:hover svg,
  &:focus-visible svg {
    transform: rotate(18deg) scale(1.04);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SelectedAddressPanel = styled.div`
  position: absolute;
  left: 1rem;
  right: 4.9rem;
  bottom: 1rem;
  z-index: 2;
  min-height: 42px;
  box-sizing: border-box;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.92);
  color: #f8fafc;
  font-size: 0.92rem;
  font-weight: 900;
  line-height: 1.28;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.28);

  @media (max-width: 640px) {
    right: 1rem;
    bottom: 4.6rem;
  }
`;

const CompassIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M15.7 6.9 13.4 13.4 6.9 15.7 9.2 9.2z" fill="#fbbf24" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="1.35" fill="currentColor" />
  </svg>
);

const SearchBoxContainer = styled.div`
  position: relative;
  width: min(520px, calc(100vw - 4rem));
  margin: 1rem;

  input {
    width: 100%;
    box-sizing: border-box;
    min-height: 48px;
    padding: 0.75rem 0.95rem;
    border: 1px solid rgba(226, 232, 240, 0.28);
    border-radius: 18px;
    background: rgba(15, 23, 42, 0.9);
    color: #f8fafc;
    font-size: 1rem;
    font-weight: 800;
    outline: none;
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);

    &::placeholder {
      color: rgba(203, 213, 225, 0.58);
    }
  }
`;

const getFallbackLocationName = (position) => {
  if (!position) {
    return 'Dropped pin';
  }

  return `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`;
};

const getAddressForPosition = (position) => {
  if (!position || !window.google?.maps?.Geocoder) {
    return Promise.resolve(getFallbackLocationName(position));
  }

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results?.[0]?.formatted_address) {
        resolve(results[0].formatted_address);
        return;
      }

      resolve(getFallbackLocationName(position));
    });
  });
};

const CreateLocation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/manage-locations');
  const defaultRadius = loadDefaultLocationRadius();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [requiredDistanceValue, setRequiredDistanceValue] = useState(defaultRadius.value);
  const [requiredDistanceUnit, setRequiredDistanceUnit] = useState(defaultRadius.unit);
  const [error, setError] = useState('');
  const [searchBox, setSearchBox] = useState(null);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedAddressLoading, setSelectedAddressLoading] = useState(false);
  const [mapZoom, setMapZoom] = useState(13);
  const [defaultCenter, setDefaultCenter] = useState({
    lat: 37.7749,
    lng: -122.4194
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  const handleSelectDistanceUnit = (nextUnit) => {
    setRequiredDistanceUnit(nextUnit);
    setRequiredDistanceValue(DISTANCE_UNITS[nextUnit].defaultValue);
  };

  useEffect(() => {
    if (!googleMapsApiKey) {
      setError('Google Maps API key is not configured');
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setDefaultCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location: ', error);
        }
      );
    }
  }, [googleMapsApiKey]);

  const handleMapClick = async (event) => {
    if (event && event.latLng) {
      const nextLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      setSelectedLocation(nextLocation);
      setSelectedAddress('');
      setSelectedAddressLoading(true);

      const address = await getAddressForPosition(nextLocation);
      setSelectedAddress(address);
      setSelectedAddressLoading(false);
    }
  };

  const handlePlacesChanged = () => {
    if (searchBox) {
      const places = searchBox.getPlaces();

      if (places && places.length > 0) {
        const place = places[0];
        const newLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };

        setSelectedLocation(newLocation);
        setDefaultCenter(newLocation);
        setSelectedAddressLoading(false);
        setSelectedAddress(place.formatted_address || place.name || '');

        if (!place.formatted_address && !place.name) {
          setSelectedAddressLoading(true);
          getAddressForPosition(newLocation).then((address) => {
            setSelectedAddress(address);
            setSelectedAddressLoading(false);
          });
        }
      }
    }
  };

  const handleCenterOnCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Current location is not available in this browser');
      return;
    }

    setLocating(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDefaultCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setMapZoom(17);
        setLocating(false);
      },
      (locationError) => {
        console.error('Error getting location: ', locationError);
        setError('Unable to get your current location');
        setLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000
      }
    );
  };

  const handleSaveLocation = async (event) => {
    event.preventDefault();

    if (!selectedLocation || !locationName.trim()) {
      setError('Please set a location and provide a name');
      return;
    }

    const requiredDistance = convertDistanceToFeet(requiredDistanceValue, requiredDistanceUnit);

    if (!requiredDistance) {
      setError('Please enter a valid radius');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.post(
        `${window.RUNTIME_CONFIG.apiUrl}/locations`,
        {
          location: selectedLocation,
          name: locationName.trim(),
          requiredDistance,
          requiredDistanceValue: Number(requiredDistanceValue),
          requiredDistanceUnit
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      navigate('/manage-locations');
    } catch (error) {
      console.error('Error saving location:', error);
      setError('Failed to save location');
      setSaving(false);
    }
  };

  return (
    <Page>
      <Shell $wide>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>New location.</Title>
            <Subtitle>Name the spot, set the radius, then drop a pin.</Subtitle>
          </HeaderText>
        </Header>

        <Form onSubmit={handleSaveLocation}>
          <Field>
            Location name
            <Input
              type="text"
              value={locationName}
              onChange={(event) => setLocationName(event.target.value)}
              placeholder="Home, Work, Gym..."
              required
            />
          </Field>
          <Field>
            Radius
            <DistanceControls>
              <Input
                type="number"
                aria-label="Radius value"
                min={getDistanceUnit(requiredDistanceUnit).min}
                step={getDistanceUnit(requiredDistanceUnit).step}
                value={requiredDistanceValue}
                onChange={(event) => setRequiredDistanceValue(event.target.value)}
                placeholder="Distance"
                required
              />
              <UnitSelector aria-label="Radius unit">
                {DISTANCE_UNIT_OPTIONS.map((option, index) => (
                  <React.Fragment key={option.value}>
                    {index > 0 && <UnitSeparator aria-hidden="true">-</UnitSeparator>}
                    <UnitOption
                      type="button"
                      $selected={requiredDistanceUnit === option.value}
                      aria-pressed={requiredDistanceUnit === option.value}
                      aria-label={`Use ${option.label}`}
                      onClick={() => handleSelectDistanceUnit(option.value)}
                    >
                      {option.label}
                    </UnitOption>
                  </React.Fragment>
                ))}
              </UnitSelector>
            </DistanceControls>
          </Field>
          {selectedLocation && (
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Location'}
            </Button>
          )}
        </Form>

        {!isMapOpen && (
          <Button type="button" onClick={() => setIsMapOpen(true)}>
            {selectedLocation ? 'Change location...' : 'Select location...'}
          </Button>
        )}

        {isMapOpen && googleMapsApiKey ? (
          <MapPanel>
            <MapContainer>
              <LoadScript
                googleMapsApiKey={googleMapsApiKey}
                version="quarterly"
                libraries={['places']}
              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={defaultCenter}
                  zoom={mapZoom}
                  onClick={handleMapClick}
                >
                  <StandaloneSearchBox
                    onLoad={ref => setSearchBox(ref)}
                    onPlacesChanged={handlePlacesChanged}
                  >
                    <SearchBoxContainer>
                      <input type="text" placeholder="Search for a location" />
                    </SearchBoxContainer>
                  </StandaloneSearchBox>

                  {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>
              </LoadScript>
              <LocateButton
                type="button"
                disabled={locating}
                aria-label="Center map on current location"
                title="Center map on current location"
                onClick={handleCenterOnCurrentLocation}
              >
                <CompassIcon />
              </LocateButton>
              {selectedLocation && (
                <SelectedAddressPanel>
                  {selectedAddressLoading ? 'Looking up address...' : selectedAddress}
                </SelectedAddressPanel>
              )}
            </MapContainer>
          </MapPanel>
        ) : isMapOpen ? (
          <Message $error>Google Maps API key is not configured. Please check your environment configuration.</Message>
        ) : null}

        {error && <Message $error>{error}</Message>}
      </Shell>
    </Page>
  );
};

export default CreateLocation;
